import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Search, FileText, AlertTriangle, FileWarning, FolderOpen, Trash2, Edit3, ExternalLink } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { formatAED, formatDate } from '@/lib/format';
import { CONTRACT_STATUS, CONTRACT_STATUS_COLOR, CONTRACT_TYPES, DOC_CATEGORIES } from '@/lib/constants';
import { EmptyState, CardGridSkeleton } from '@/components/ui/states';
import ContractSlideOver from '@/components/contracts/ContractSlideOver';
import DocumentSlideOver from '@/components/contracts/DocumentSlideOver';

const TABS = [{ key: 'contracts', label: 'Contracts' }, { key: 'documents', label: 'Documents' }];

export default function ContractsPage() {
    const { profile } = useAuth();
    const navigate = useNavigate();
    const { tab } = useParams();
    const active = tab || 'contracts';

    const [contracts, setContracts] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [developers, setDevelopers] = useState([]);
    const [pms, setPMs] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterCat, setFilterCat] = useState('');

    const [showContract, setShowContract] = useState(false);
    const [editingContract, setEditingContract] = useState(null);
    const [showDoc, setShowDoc] = useState(false);
    const [editingDoc, setEditingDoc] = useState(null);

    const load = useCallback(async () => {
        if (!profile?.organization_id) return;
        setLoading(true);
        const [c, d, ct, dv, pm, pr] = await Promise.all([
            supabase.from('contracts').select('*').is('deleted_at', null).order('created_at', { ascending: false }),
            supabase.from('documents').select('*').is('deleted_at', null).order('created_at', { ascending: false }),
            supabase.from('pipeline_contacts').select('id,full_name').is('deleted_at', null).order('full_name'),
            supabase.from('developers').select('id,company_name').is('deleted_at', null).order('company_name'),
            supabase.from('property_managers').select('id,company_name').is('deleted_at', null).order('company_name'),
            supabase.from('developer_projects').select('id,project_name,developer_id').is('deleted_at', null).order('project_name'),
        ]);
        setContracts(c.data || []);
        setDocuments(d.data || []);
        setContacts(ct.data || []);
        setDevelopers(dv.data || []);
        setPMs(pm.data || []);
        setProjects(pr.data || []);
        setLoading(false);
    }, [profile?.organization_id]);
    useEffect(() => { load(); }, [load]);

    const expSoon = useMemo(() => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const cutoff = new Date(today); cutoff.setDate(cutoff.getDate() + 60);
        return contracts.filter((c) => c.expiry_date && new Date(c.expiry_date) <= cutoff && new Date(c.expiry_date) >= today && c.status !== 'Terminated');
    }, [contracts]);
    const expired = useMemo(() => contracts.filter((c) => c.expiry_date && new Date(c.expiry_date) < new Date() && c.status !== 'Terminated'), [contracts]);

    const filteredContracts = contracts.filter((c) => {
        if (filterStatus && c.status !== filterStatus) return false;
        if (filterType && c.contract_type !== filterType) return false;
        if (q && !`${c.title} ${c.contract_type}`.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    });
    const filteredDocs = documents.filter((d) => {
        if (filterCat && d.category !== filterCat) return false;
        if (q && !`${d.name} ${d.category}`.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    });

    const onDeleteContract = async (id) => {
        if (!window.confirm('Delete this contract?')) return;
        const { error } = await supabase.from('contracts').update({ deleted_at: new Date().toISOString() }).eq('id', id);
        if (error) return toast.error(error.message);
        toast.success('Contract deleted'); load();
    };
    const onDeleteDoc = async (id) => {
        if (!window.confirm('Delete this document?')) return;
        const { error } = await supabase.from('documents').update({ deleted_at: new Date().toISOString() }).eq('id', id);
        if (error) return toast.error(error.message);
        toast.success('Document deleted'); load();
    };

    return (
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6" data-testid="contracts-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-aks-text">Contracts & Documents</h1>
                    <p className="text-[13px] text-gray-500 mt-1">Centralized agreement and document vault</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-9 pl-9 pr-3 rounded-md border border-gray-300 text-[13px] outline-none focus:border-aks-gold w-56" data-testid="contracts-search" />
                    </div>
                    {active === 'contracts' ? (
                        <button onClick={() => { setEditingContract(null); setShowContract(true); }} className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-aks-gold text-aks-navy font-semibold text-[13px]" data-testid="add-contract-btn"><Plus className="h-4 w-4" /> New contract</button>
                    ) : (
                        <button onClick={() => { setEditingDoc(null); setShowDoc(true); }} className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-aks-gold text-aks-navy font-semibold text-[13px]" data-testid="add-doc-btn"><Plus className="h-4 w-4" /> Upload document</button>
                    )}
                </div>
            </div>

            <div className="mt-6 border-b border-gray-200 flex gap-6">
                {TABS.map((t) => (
                    <button key={t.key} onClick={() => navigate(`/contracts/${t.key === 'contracts' ? '' : t.key}`)} className={`pb-3 -mb-px border-b-2 text-[14px] font-semibold ${active === t.key ? 'border-aks-gold text-aks-navy' : 'border-transparent text-gray-500 hover:text-aks-navy'}`} data-testid={`ctab-${t.key}`}>{t.label}</button>
                ))}
            </div>

            {active === 'contracts' && (
                <div className="mt-6 space-y-6">
                    {(expired.length > 0 || expSoon.length > 0) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="contract-alerts">
                            {expired.length > 0 && (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                                    <div>
                                        <div className="font-bold text-red-700 text-[14px]">{expired.length} expired contract{expired.length === 1 ? '' : 's'}</div>
                                        <div className="text-[12px] text-red-600/80 mt-0.5">Review and renew or terminate.</div>
                                    </div>
                                </div>
                            )}
                            {expSoon.length > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                                    <FileWarning className="h-5 w-5 text-amber-600 mt-0.5" />
                                    <div>
                                        <div className="font-bold text-amber-700 text-[14px]">{expSoon.length} expiring within 60 days</div>
                                        <div className="text-[12px] text-amber-600/80 mt-0.5">Plan renewals before expiry.</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-8 px-2 rounded-md border border-gray-300 text-[12px]"><option value="">All statuses</option>{CONTRACT_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}</select>
                        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="h-8 px-2 rounded-md border border-gray-300 text-[12px]"><option value="">All types</option>{CONTRACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select>
                    </div>

                    {loading ? <CardGridSkeleton count={6} /> : filteredContracts.length === 0 ? (
                        <EmptyState icon={FileText} title="No contracts yet" message="Track MOUs, SPAs, rental and management agreements with expiry alerts." actionLabel="New contract" onAction={() => { setEditingContract(null); setShowContract(true); }} />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="contracts-grid">
                            {filteredContracts.map((c) => {
                                const expDays = c.expiry_date ? Math.floor((new Date(c.expiry_date) - new Date()) / 86400000) : null;
                                const expColor = expDays == null ? '#9CA3AF' : expDays < 0 ? '#EF4444' : expDays <= 60 ? '#F59E0B' : '#10B981';
                                const sc = CONTRACT_STATUS_COLOR[c.status] || '#9CA3AF';
                                return (
                                    <div key={c.id} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md hover:border-aks-gold/40 transition" data-testid={`contract-card-${c.id}`}>
                                        <div className="flex items-start gap-3">
                                            <div className="h-10 w-10 rounded-md bg-aks-navy/10 text-aks-navy flex items-center justify-center"><FileText className="h-5 w-5" /></div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold text-aks-text truncate">{c.title}</div>
                                                <div className="text-[12px] text-gray-500 truncate">{c.contract_type}</div>
                                            </div>
                                            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: `${sc}1A`, color: sc, border: `1px solid ${sc}55` }}>{c.status}</span>
                                        </div>
                                        <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
                                            <div><div className="text-gray-400">Date</div><div className="font-semibold text-aks-navy">{formatDate(c.contract_date) || '—'}</div></div>
                                            <div><div className="text-gray-400">Expiry</div><div className="font-semibold" style={{ color: expColor }}>{c.expiry_date ? (expDays < 0 ? 'Expired' : `${expDays}d`) : '—'}</div></div>
                                            <div><div className="text-gray-400">Value</div><div className="font-semibold text-aks-navy">{c.value_aed ? formatAED(c.value_aed) : '—'}</div></div>
                                        </div>
                                        <div className="mt-3 flex items-center justify-between">
                                            <div className="flex items-center gap-1">
                                                {c.file_url && <a href={c.file_url} target="_blank" rel="noreferrer" className="h-8 px-2 rounded-md border border-gray-300 hover:border-aks-gold text-[11px] inline-flex items-center gap-1" data-testid={`contract-view-${c.id}`}><ExternalLink className="h-3 w-3" /> View file</a>}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => { setEditingContract(c); setShowContract(true); }} className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-300 hover:border-aks-gold" data-testid={`contract-edit-${c.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                                                {profile?.role === 'admin' && (
                                                    <button onClick={() => onDeleteContract(c.id)} className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-300 hover:border-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {active === 'documents' && (
                <div className="mt-6 space-y-4">
                    <div className="flex flex-wrap gap-2">
                        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="h-8 px-2 rounded-md border border-gray-300 text-[12px]"><option value="">All categories</option>{DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                    </div>
                    {loading ? <CardGridSkeleton count={6} /> : filteredDocs.length === 0 ? (
                        <EmptyState icon={FolderOpen} title="No documents yet" message="Upload trade licenses, KYC files, marketing assets and HR docs." actionLabel="Upload document" onAction={() => { setEditingDoc(null); setShowDoc(true); }} />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="documents-grid">
                            {filteredDocs.map((d) => {
                                const expDays = d.expiry_date ? Math.floor((new Date(d.expiry_date) - new Date()) / 86400000) : null;
                                const expColor = expDays == null ? '#9CA3AF' : expDays < 0 ? '#EF4444' : expDays <= 60 ? '#F59E0B' : '#10B981';
                                return (
                                    <div key={d.id} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md hover:border-aks-gold/40 transition" data-testid={`doc-card-${d.id}`}>
                                        <div className="flex items-start gap-3">
                                            <div className="h-10 w-10 rounded-md bg-aks-gold/15 text-aks-navy flex items-center justify-center"><FolderOpen className="h-5 w-5" /></div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold text-aks-text truncate">{d.name}</div>
                                                <div className="text-[12px] text-gray-500 truncate">{d.category}</div>
                                            </div>
                                        </div>
                                        {d.expiry_date && (
                                            <div className="mt-3 text-[11px]"><span className="text-gray-400">Expires:</span> <span className="font-semibold" style={{ color: expColor }}>{formatDate(d.expiry_date)} ({expDays < 0 ? 'expired' : `${expDays}d`})</span></div>
                                        )}
                                        <div className="mt-3 flex items-center justify-between">
                                            <a href={d.file_url} target="_blank" rel="noreferrer" className="h-8 px-2 rounded-md border border-gray-300 hover:border-aks-gold text-[11px] inline-flex items-center gap-1" data-testid={`doc-view-${d.id}`}><ExternalLink className="h-3 w-3" /> Open</a>
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => { setEditingDoc(d); setShowDoc(true); }} className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-300 hover:border-aks-gold" data-testid={`doc-edit-${d.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                                                {profile?.role === 'admin' && (
                                                    <button onClick={() => onDeleteDoc(d.id)} className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-300 hover:border-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <ContractSlideOver open={showContract} onClose={() => setShowContract(false)} contract={editingContract} onSaved={load} contacts={contacts} developers={developers} pms={pms} projects={projects} />
            <DocumentSlideOver open={showDoc} onClose={() => setShowDoc(false)} document={editingDoc} onSaved={load} contacts={contacts} developers={developers} pms={pms} projects={projects} />
        </div>
    );
}
